import { useState, useEffect } from 'react'
import { TabManager } from './components/tabs/TabManager'
import { LoginPage } from './components/auth/LoginPage'
import { ConnectionStatus } from './components/status/ConnectionStatus'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Marketplace } from './windows/Marketplace'
import { MiniChatWindow } from './components/AIAssistant/MiniChatWindow'
import { logInfo } from './utils/errorLogger'
import './styles/app.css'

const AppContent: React.FC = () => {
  const { isAuthenticated, loading, login, logout, userType, userName } = useAuth()
  const [miniChatOpen, setMiniChatOpen] = useState(false)

  // Support deep links: #/gv/<projectId> opens Girasoles View directly
  // MUST be declared before any conditional returns (Rules of Hooks)
  const [deepLinkProject, setDeepLinkProject] = useState<string | null>(null)
  useEffect(() => {
    const match = window.location.hash.match(/^#\/gv\/(.+)$/)
    if (match) {
      setDeepLinkProject(match[1])
      window.location.hash = ''
    }
  }, [])

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading Girasoles CAD...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        onLoginSuccess={(userId, userName, authToken, userType, isAdmin) => {
          login(userId, userName, authToken, userType, isAdmin)
          logInfo(`Login successful (userType: ${userType}, admin: ${isAdmin})`, 'App')
        }}
      />
    )
  }

  if (userType === 'farm') {
    return (
      <div className="app" data-testid="app-container">
        <ConnectionStatus />

        <button
          className="admin-view-switcher"
          onClick={() => logout()}
          title={userName ? `Logout ${userName}` : 'Logout'}
          aria-label="Logout"
        >
          <span className="switcher-icon">↩</span>
          <span className="switcher-text">Logout</span>
        </button>

        <div className="app-content">
          <Marketplace />
        </div>
        <button
          className="mini-chat-toggle"
          onClick={() => setMiniChatOpen(true)}
          aria-label="Open AI Assistant"
          title="AI Assistant"
        >
          💬
        </button>
        <MiniChatWindow isOpen={miniChatOpen} onClose={() => setMiniChatOpen(false)} />
      </div>
    )
  }

  // Show regular CAD workbench (admin panel is now a tab in the bottom bar)
  return (
    <div className="app" data-testid="app-container">
      {/* Connection Status Indicator */}
      <ConnectionStatus />



      {/* Main Application Content */}
      <div className="app-content">
        <TabManager deepLinkProjectId={deepLinkProject} />
      </div>
      <button
        className="mini-chat-toggle"
        onClick={() => setMiniChatOpen(true)}
        aria-label="Open AI Assistant"
        title="AI Assistant"
      >
        💬
      </button>
      <MiniChatWindow isOpen={miniChatOpen} onClose={() => setMiniChatOpen(false)} />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
